import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Repeat, Plus, Check, Loader2 } from "lucide-react";
import { api, money, USER_ID } from "./api";
import { addPick, pickListToast } from "./picks";
import { VegDot } from "./components";

export const getMealPrefs = () => {
  try { return JSON.parse(localStorage.getItem("xp_meal_prefs")) || {}; } catch { return {}; }
};
export const setMealPrefs = (p) => localStorage.setItem("xp_meal_prefs", JSON.stringify(p));

export const ALLERGENS = [
  { id: "dairy", label: "DAIRY-FREE" },
  { id: "nuts", label: "NUT-FREE" },
  { id: "gluten", label: "GLUTEN-FREE" },
];
const ALLERGEN_LABELS = { dairy: "DAIRY-FREE", nuts: "NUT-FREE", gluten: "GLUTEN-FREE" };

const PrefPill = ({ active, children, onClick, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`rounded-full px-3.5 py-2 text-[10px] font-semibold border transition-colors ${active ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-[#1A1A1A] text-white/70 border-white/10"}`}>
    {children}
  </button>
);

export const MealPrefsBar = ({ prefs, onChange, alwaysOpen = false }) => {
  const [open, setOpen] = useState(alwaysOpen);
  const set = (p) => {
    const n = { ...prefs, ...p };
    setMealPrefs(n);
    onChange(n);
  };
  const toggleAllergen = (id) => {
    const cur = prefs.allergens || [];
    set({ allergens: cur.includes(id) ? cur.filter((a) => a !== id) : [...cur, id] });
  };
  const hasCustom = !!prefs.vegetarian || !!prefs.spice || (prefs.allergens || []).length > 0;
  const summary = [
    prefs.vegetarian ? "VEG" : null,
    prefs.spice ? prefs.spice.toUpperCase() : null,
    ...(prefs.allergens || []).map((a) => ALLERGEN_LABELS[a]),
  ].filter(Boolean).join(" · ");

  if (!open) {
    return (
      <button data-testid="meal-prefs-toggle" onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-[#121212] px-4 py-3 mb-3 text-left">
        <span className="text-[9px] tracking-[0.25em] text-white/40 font-bold shrink-0">YOUR TABLE'S PREFERENCES</span>
        <span data-testid="meal-prefs-summary" className="text-[10px] font-bold text-[#D4AF37] truncate">
          {hasCustom ? summary : "SET VEG · SPICE · ALLERGIES"}
        </span>
      </button>
    );
  }
  return (
    <div data-testid="meal-prefs-bar" className="rounded-xl border border-white/10 bg-[#121212] p-4 mb-3">
      <p className="text-[9px] tracking-[0.25em] text-white/40 font-bold mb-2">DIET</p>
      <div className="flex gap-2 mb-3 flex-wrap">
        <PrefPill testId="pref-nonveg" active={!prefs.vegetarian} onClick={() => set({ vegetarian: false })}>NON-VEG</PrefPill>
        <PrefPill testId="pref-veg" active={!!prefs.vegetarian} onClick={() => set({ vegetarian: true })}>VEGETARIAN</PrefPill>
      </div>
      <p className="text-[9px] tracking-[0.25em] text-white/40 font-bold mb-2">SPICE</p>
      <div className="flex gap-2 mb-3 flex-wrap">
        {["mild", "medium", "hot"].map((s) => (
          <PrefPill key={s} testId={`pref-spice-${s}`} active={(prefs.spice || "medium") === s} onClick={() => set({ spice: s })}>{s.toUpperCase()}</PrefPill>
        ))}
      </div>
      <p className="text-[9px] tracking-[0.25em] text-white/40 font-bold mb-2">ALLERGIES & PREFERENCES</p>
      <div className="flex gap-2 flex-wrap">
        {ALLERGENS.map((a) => (
          <PrefPill key={a.id} testId={`pref-allergen-${a.id}`} active={(prefs.allergens || []).includes(a.id)} onClick={() => toggleAllergen(a.id)}>{a.label}</PrefPill>
        ))}
      </div>
      <p className="text-[10px] text-white/40 mt-3">Remembered on this tablet for your whole visit — every suggested meal follows these.</p>
      {!alwaysOpen && (
        <button data-testid="meal-prefs-done" onClick={() => setOpen(false)}
          className="w-full mt-3 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-bold rounded-full py-2.5 tracking-widest">
          DONE
        </button>
      )}
    </div>
  );
};

const Diff = ({ d }) => (
  <span className={`text-[10px] font-black ${d > 0 ? "text-[#E25822]" : d < 0 ? "text-green-400" : "text-white/40"}`}>
    {d > 0 ? `+${money(d)}` : d < 0 ? `−${money(-d)}` : "SAME PRICE"}
  </span>
);

export const AltPanel = ({ role, current, mainSlug, prefs, onSelect, onClose }) => {
  const [alts, setAlts] = useState(null);
  useEffect(() => {
    api.post("/meal/alternatives", {
      user_id: USER_ID, role, current_slug: current.slug, main_slug: mainSlug,
      vegetarian: !!prefs.vegetarian, spice: prefs.spice || "medium", allergens: prefs.allergens || [],
    }).then((r) => setAlts(r.data.alternatives)).catch(() => setAlts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, current.slug]);
  return (
    <div data-testid={`meal-alts-${role}`} className="rounded-xl bg-[#0C0A07] border border-[#D4AF37]/30 p-3 mx-3 mb-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-bold">PICK A REPLACEMENT</p>
        <button data-testid={`meal-alts-close-${role}`} onClick={onClose} className="text-[9px] font-bold tracking-widest text-white/50">CANCEL</button>
      </div>
      {!alts && <p className="text-xs text-white/40 py-2 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Finding matches…</p>}
      {alts && alts.length === 0 && <p className="text-xs text-white/40 py-2">No compatible alternatives match your preferences.</p>}
      {alts && alts.map((a) => (
        <div key={a.slug} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
          <img src={a.image} alt={a.name} className="w-9 h-9 rounded-lg object-cover shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="text-xs flex items-start gap-1.5">
              <span className="shrink-0 mt-[3px]"><VegDot veg={a.vegetarian} /></span>
              <span className="leading-snug min-w-0 break-words">{a.name}</span>
            </span>
            <span className="text-[10px] text-white/50">{money(a.price)} · <Diff d={a.price_diff} /></span>
          </span>
          <button data-testid={`meal-alt-${a.slug}`} onClick={() => onSelect(a)}
            className="shrink-0 text-[9px] font-black tracking-widest bg-[#D4AF37] text-black rounded-full px-3.5 py-1.5">
            SELECT
          </button>
        </div>
      ))}
    </div>
  );
};

export default function SuggestedMeal({ mainSlug, prefs = {}, trigger = 0, autoLoad = false }) {
  const [meal, setMeal] = useState(null);
  const [addonsOn, setAddonsOn] = useState(() => new Set());
  const [replacing, setReplacing] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = useCallback(async (exclude = []) => {
    setLoading(true);
    setReplacing(null);
    try {
      const r = await api.post("/meal/suggest", {
        user_id: USER_ID, main: mainSlug || null, vegetarian: !!prefs.vegetarian,
        spice: prefs.spice || "medium", allergens: prefs.allergens || [], exclude,
      });
      setMeal(r.data);
      setAddonsOn(new Set());
    } catch {
      setMeal(null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainSlug, JSON.stringify(prefs)]);

  useEffect(() => {
    if (autoLoad) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, generate]);
  useEffect(() => {
    if (trigger > 0) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  if (!meal) {
    return loading ? <p className="text-xs text-white/40 px-1 py-3 flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Cooking up a meal…</p> : null;
  }

  const regenerate = () => {
    const slugs = [...meal.items, ...meal.addons].map((x) => x.dish.slug).filter((s) => s !== mainSlug);
    generate(slugs);
  };

  const swap = async (role, alt, isAddon) => {
    if (role === "main") {
      // a new main can change the whole meal shape (biryani → gravy rule) — re-derive from the server
      setLoading(true);
      setReplacing(null);
      try {
        const r = await api.post("/meal/suggest", {
          user_id: USER_ID, main: alt.slug, vegetarian: !!prefs.vegetarian,
          spice: prefs.spice || "medium", allergens: prefs.allergens || [],
        });
        setMeal(r.data);
        setAddonsOn(new Set());
      } catch {}
      setLoading(false);
      return;
    }
    const upd = { ...alt, category: alt.category };
    if (isAddon) setMeal({ ...meal, addons: meal.addons.map((a) => (a.role === role ? { ...a, dish: { ...a.dish, ...upd } } : a)) });
    else {
      const items = meal.items.map((i) => (i.role === role ? { ...i, dish: { ...i.dish, ...upd } } : i));
      setMeal({ ...meal, items, total: Math.round(items.reduce((s, i) => s + i.dish.price, 0) * 100) / 100 });
    }
    setReplacing(null);
  };

  const toggleAddon = (role) => {
    const n = new Set(addonsOn);
    n.has(role) ? n.delete(role) : n.add(role);
    setAddonsOn(n);
  };

  const selectedAddons = meal.addons.filter((a) => addonsOn.has(a.role));

  const Row = ({ entry, isAddon }) => {
    const { role, label, dish } = entry;
    const on = !isAddon || addonsOn.has(role);
    return (
      <div>
        <div className={`flex items-center gap-3 p-3 ${on ? "" : "opacity-50"}`} data-testid={`meal-row-${role}`}>
          {isAddon && (
            <button data-testid={`meal-addon-${role}`} onClick={() => toggleAddon(role)} aria-label={`Add ${label.toLowerCase()}`}
              className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${on ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"}`}>
              {on ? <Check size={13} className="text-black" /> : <Plus size={13} className="text-white/50" />}
            </button>
          )}
          <img src={dish.image} alt={dish.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
          <span className="flex-1 min-w-0">
            <span className={`block text-[9px] tracking-widest font-bold ${role === "gravy" ? "text-[#F0C742]" : "text-[#D4AF37]"}`}>{label}</span>
            <span className="text-xs flex items-start gap-1.5 mt-0.5">
              <span className="shrink-0 mt-[3px]"><VegDot veg={dish.vegetarian} /></span>
              <span className="line-clamp-3 leading-snug min-w-0">{dish.name}</span>
            </span>
            <span className="block sm:hidden text-xs text-[#D4AF37] font-bold mt-0.5">{money(dish.price)}</span>
          </span>
          <span className="hidden sm:block text-xs text-[#D4AF37] font-bold shrink-0">{money(dish.price)}</span>
          {!(role === "main" && mainSlug) && (
            <button data-testid={`meal-replace-${role}`} onClick={() => setReplacing(replacing === role ? null : role)}
              aria-label={`Replace ${dish.name}`}
              className="shrink-0 flex items-center gap-1 text-[9px] font-bold tracking-widest text-white/70 border border-white/20 rounded-full px-2.5 py-1.5">
              <Repeat size={12} /> <span className="hidden sm:inline">REPLACE</span>
            </button>
          )}
        </div>
        {replacing === role && (
          <AltPanel role={role} current={entry.dish} mainSlug={(meal.items[0] || {}).dish?.slug} prefs={prefs}
            onSelect={(alt) => swap(role, alt, isAddon)} onClose={() => setReplacing(null)} />
        )}
      </div>
    );
  };

  return (
    <div data-testid="suggested-meal" className="rounded-2xl bg-[#121212] border border-[#D4AF37]/30">
      <div className="divide-y divide-white/5">
        {meal.items.map((i) => <Row key={i.role} entry={i} />)}
      </div>
      {meal.is_biryani && (
        <p className="px-3 pb-1 text-[10px] text-white/45">Every biryani comes with one gravy — swap it anytime. No extra rice needed.</p>
      )}
      {(meal.unavailable || []).length > 0 && (
        <p data-testid="meal-pref-note" className="px-3 pb-1 text-[10px] text-white/45">
          Some items ({meal.unavailable.join(", ")}) were left out to match your preferences.
        </p>
      )}
      {meal.addons.length > 0 && (
        <>
          <p className="px-3 pt-3 text-[9px] tracking-[0.25em] text-white/40 font-bold">OPTIONAL ADD-ONS — ONLY SELECTED ITEMS COUNT</p>
          <div className="divide-y divide-white/5">
            {meal.addons.map((a) => <Row key={a.role} entry={a} isAddon />)}
          </div>
        </>
      )}
      <div className="flex justify-between items-center p-4 border-t border-white/10">
        <span className="text-xs text-white/55">Tax and anything else is added at the till.</span>
      </div>
      <div className="p-3 flex gap-2">
        <button data-testid="meal-regenerate" onClick={regenerate} disabled={loading}
          className="flex-1 flex items-center justify-center gap-1.5 border border-white/20 rounded-full py-3 text-xs font-bold tracking-widest disabled:opacity-60">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> REGENERATE MEAL
        </button>
        <button data-testid="meal-add-order"
          onClick={() => {
            [...meal.items.map((i) => i.dish), ...selectedAddons.map((a) => a.dish)].forEach((d) => addPick(d, 1, true));
            pickListToast("Meal added to your order list");
          }}
          className="flex-1 bg-[#D4AF37] text-black rounded-full py-3 text-xs font-bold tracking-widest">
          ADD TO ORDER LIST
        </button>
      </div>
    </div>
  );
}
